import { dataLayerConfig, TAGLink } from '@aspendental/shared-components-web';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Button, CircularProgress, Stack, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';
import router from 'next/router';
import { useEffect, useState } from 'react';
import { useWizard } from 'react-use-wizard';
import ConfirmAppointmentModal from '../Confirmation/ConfirmAppointmentModal';
import { mapCreditResponseCodes, mapDentalConditionCodes, mapDentalSolutionCodes } from '../Forms/PatientInformation/PatientInformation.helpers';
import LoadingSpinnerModal from '../LoadingSpinnerModal';
import { getUnansweredQuestions } from '../QuestionnaireStep/QuestionnaireStep.helpers';
import { SubmitAppointmentErrorCode } from '@src/constants';
import { submitConsentManagement } from '@src/lib/client';
import { getLeadsScore } from '@src/services/getLeadsScore';
import { submitAppointment } from '@src/services/submitAppointment';
import { updateLead } from '@src/services/updateLead';
import { useAppStore, usePatientInformationStore, useScheduleQuestionsStore, useStepManagerStore } from '@src/store';
import useAppointmentInformationStore from '@src/store/appointment-information.store';
function ConsultAlreadyScheduledDescription() {
  const {
    t
  } = useTranslation('index');
  const theme = useTheme();
  return <Typography variant="bodyLargeBook" data-sentry-element="Typography" data-sentry-component="ConsultAlreadyScheduledDescription" data-sentry-source-file="StepNavigation.tsx">
			{t('wantToCancelOrReschedule')} <br />
			{t('pleaseCallAt')}
			<TAGLink href={`tel:888-651-9950`} linkSx={{
      color: theme.palette.text.interactive,
      textDecoration: 'none'
    }} data-sentry-element="TAGLink" data-sentry-source-file="StepNavigation.tsx">
				{' '}
				888-651-9950{' '}
			</TAGLink>
			{t('forAssistance')}
		</Typography>;
}
function ConsultAppointmentExpiredDescription() {
  const {
    t
  } = useTranslation('index');
  return <Typography variant="bodyLargeBook" data-sentry-element="Typography" data-sentry-component="ConsultAppointmentExpiredDescription" data-sentry-source-file="StepNavigation.tsx">{t('chooseAnotherTimeApology')}</Typography>;
}
export default function StepNavigation() {
  const {
    questionsAnswers,
    questions,
    setUnansweredQuestions,
    setScheduleInfoModal
  } = useScheduleQuestionsStore(store => store);
  const {
    locale
  } = router;
  const languagePreference = locale === 'es-US' ? 'Spanish' : 'English';

  // Lead ID:
  const {
    leadId,
    salesforceGuid,
    setSalesforceS20,
    setSalesforceTier
  } = usePatientInformationStore();
  const {
    chosenAppointmentData,
    chosenAppointmentKey,
    setNoChosenAppointmentError
  } = useAppointmentInformationStore();
  const {
    t
  } = useTranslation('index');
  // Add loading modal state
  const [showConfirmingModal, setShowConfirmingModal] = useState(false);
  const {
    activeStep,
    nextStep,
    previousStep,
    isFirstStep
  } = useWizard();
  const {
    sessionId,
    oneTrust
  } = useAppStore(store => store);
  const {
    setIsTransitioning,
    isTransitioning
  } = useStepManagerStore(store => store);
  const [formId, setFormId] = useState<string>();
  useEffect(() => {
    setFormId(activeStep === 1 ? `step-${activeStep}-form` : undefined);
  }, [activeStep]);
  const handleBackStep = () => {
    if (isTransitioning) {
      return;
    }
    if (activeStep === 2) {
      setNoChosenAppointmentError(false);
    }
    previousStep();
  };
  const handleNextStep = () => {
    if (isTransitioning) {
      return;
    }
    if (activeStep === 0) {
      setIsTransitioning(true);
      const unanswered = getUnansweredQuestions(questions, questionsAnswers);
      setUnansweredQuestions(unanswered);
      if (unanswered?.length > 0) {
        setIsTransitioning(false);
        return;
      }
      const dentalConditionCodes = mapDentalConditionCodes(questionsAnswers.current_dental_condition);
      const dentalSolutionCodes = mapDentalSolutionCodes(questionsAnswers.current_dental_solutions);
      const creditResponseValue = questionsAnswers.current_credit_score;
      let creditResponseArray: string[] = [];
      if (Array.isArray(creditResponseValue)) {
        creditResponseArray = creditResponseValue.flat();
      } else if (creditResponseValue) {
        creditResponseArray = [creditResponseValue];
      }
      const creditResponseCodes = mapCreditResponseCodes(creditResponseArray);
      dataLayerConfig({
        event: 'dental_condition_form',
        step_sequence: 2,
        lead_id: null,
        Q1_current_condition_code: dentalConditionCodes.join(', '),
        Q2_current_dental_solution_code: dentalSolutionCodes.join(', '),
        Q3_current_self_reported_response_code: creditResponseCodes.join(', '),
        lead_tier: null,
        lead_segment: null,
        language_preference: locale ?? 'en-US'
      });
      setIsTransitioning(false);
      nextStep();
    }
    if (activeStep === 2) {
      if (!chosenAppointmentData) {
        setNoChosenAppointmentError(true);
        return;
      } else {
        // Show the confirming modal
        setShowConfirmingModal(true);
        setIsTransitioning(true);
        setTimeout(() =>
        // GA4: 2.4
        getLeadsScore({
          leadId,
          salesforceId: salesforceGuid ?? '',
          sessionId
        }).then(data => {
          if (data?.score?.lead_segment20) {
            setSalesforceS20(data.score.lead_segment20);
          }
          if (data?.score?.tier) {
            setSalesforceTier(`T${data.score.tier}`);
          }
        }).catch(async error => {
          console.error('Error getting Leads Score:', error);
        }).finally(() => {
          scheduleAppointment(oneTrust);
        }), 2500);
        setIsTransitioning(false);
        return;
      }
    }
  };
  const scheduleAppointment = oneTrust => {
    updateLead({
      leadId,
      language: languagePreference,
      sessionId
    });
    submitAppointment({
      consultId: chosenAppointmentKey!,
      leadId,
      sessionId
    }).then(async data => {
      if (data) {
        // Add consent management call after successful appointment submission
        if (oneTrust) {
          try {
            const patientData = usePatientInformationStore.getState();
            await submitConsentManagement(salesforceGuid, {
              Email: patientData.email,
              Mobile: patientData.phone,
              FirstName: patientData.firstName,
              LastName: patientData.lastName,
              DateOfBirth: patientData.dateOfBirth,
              Address: patientData.address,
              City: patientData.city,
              State: patientData.state,
              Zip: patientData.zipCode
            });
            console.log('Consent management submitted successfully');
          } catch (consentError) {
            console.error('Failed to submit consent management:', consentError);
            // Don't block the user flow if consent fails
          }
        }
        // Hide the modal before proceeding
        setShowConfirmingModal(false);
        nextStep();
      }
    }).catch(async error => {
      console.error('Error submitting appointment:', error);
      // Hide the modal on error
      setShowConfirmingModal(false);
      if (error.status === SubmitAppointmentErrorCode.CONSULT_SCHEDULED) {
        setScheduleInfoModal({
          title: t('consultAlreadyScheduled'),
          renderDescription: () => <ConsultAlreadyScheduledDescription />,
          buttonText: 'Cancel',
          buttonOnClick: () => {
            setScheduleInfoModal(null);
          }
        });
      } else if (error.status === SubmitAppointmentErrorCode.NO_APPOINTMENTS) {
        setScheduleInfoModal({
          title: t('appointmentExpired'),
          renderDescription: () => <ConsultAppointmentExpiredDescription />,
          buttonText: t('cancel'),
          buttonOnClick: () => {
            setScheduleInfoModal(null);
          }
        });
      }
      setIsTransitioning(false);
    });
  };
  const getCTAButtonLabel = () => {
    if (activeStep === 2) return `${t('confirmConsultation')}`;
    return `${t('continue')}`;
  };
  return <>
			<LoadingSpinnerModal open={activeStep === 1 && isTransitioning} message={t('pleaseWait')} dataTestId="loading-modal-please-wait" data-sentry-element="LoadingSpinnerModal" data-sentry-source-file="StepNavigation.tsx" />
			<ConfirmAppointmentModal open={showConfirmingModal} data-sentry-element="ConfirmAppointmentModal" data-sentry-source-file="StepNavigation.tsx" />
			<Stack alignItems="center" justifyContent="center" data-sentry-element="Stack" data-sentry-source-file="StepNavigation.tsx">
				<Stack direction="row" spacing={1.5} alignItems="center" data-sentry-element="Stack" data-sentry-source-file="StepNavigation.tsx">
					{!isFirstStep && <Button size="M" variant="text" onClick={handleBackStep} disabled={isTransitioning}>
							<ChevronLeftIcon color={isTransitioning ? 'disabled' : 'primary'} />
							<Typography color={isTransitioning ? 'text.disabled' : 'text.primary'} variant="bodyLargeBook">
								{t('back')}
							</Typography>
						</Button>}

					<Button type={formId === 'step-1-form' ? 'submit' : 'button'} form={formId} variant="primaryAccent1" data-test-id="schedule-layout-button" size="M" onClick={handleNextStep} data-sentry-element="Button" data-sentry-source-file="StepNavigation.tsx">
						{isTransitioning && <CircularProgress sx={{
            mr: 1
          }} size={20} />}
						{getCTAButtonLabel()}
					</Button>
				</Stack>
			</Stack>
		</>;
}